import { useEffect, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';

/**
 * Detects when a newer version of the app has been deployed while the user has
 * the tab open, and prompts them to reload.
 *
 * How it works:
 *  - On mount we read the buildId from /version.json (written at build time by
 *    scripts/generate-version.js) and remember it as the "loaded" version.
 *  - We re-check periodically, and whenever the tab regains focus/visibility.
 *  - If the live buildId differs, we show a single persistent toast with a
 *    "Reload" button. Reloading pulls index.html (served no-cache) and the new
 *    hashed chunks.
 *
 * This complements the automatic ChunkLoadError reload (utils/chunkReload):
 * that handles stale tabs that *navigate*, this handles stale tabs that just
 * sit idle. index.html no-cache alone only helps on an actual reload.
 */

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.buildId === 'string' ? data.buildId : null;
  } catch {
    // Offline or transient failure — ignore, we'll try again next tick.
    return null;
  }
}

export function useVersionCheck(): void {
  const { showToast } = useToast();
  const loadedBuildId = useRef<string | null>(null);
  const promptShown = useRef(false);
  const checking = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // Skip if the prompt is already up, or another check is mid-flight —
      // focus + visibilitychange fire together when returning to a tab and
      // would otherwise show the toast twice.
      if (promptShown.current || checking.current) return;
      checking.current = true;

      try {
        const current = await fetchBuildId();
        if (cancelled || !current || current === 'dev') return;

        // First successful read establishes the baseline.
        if (loadedBuildId.current === null) {
          loadedBuildId.current = current;
          return;
        }

        if (current !== loadedBuildId.current) {
          promptShown.current = true;
          showToast('A new version of Radio Vault is available.', 'info', {
            persistent: true,
            action: {
              label: 'Reload',
              onClick: () => window.location.reload(),
            },
          });
        }
      } finally {
        checking.current = false;
      }
    };

    // Initial baseline read.
    check();

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [showToast]);
}

export default useVersionCheck;
